import React from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DatePicker = ({ label, value, onChange, error, required, futureNotAllowed = false }) => {
  const today = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const formatDate = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // auto-insert slashes while typing and call onChange when full valid date entered
  const handleChangeRaw = (e) => {
    // Handle cases where onChangeRaw may be called without a standard event
    // (e.g. when selecting from calendar). Use the raw string if provided.
    const raw = e?.target?.value ?? (typeof e === "string" ? e : "");
    if (!raw) {
      // if user cleared the input, propagate null
      if (e?.target && onChange) onChange(null);
      return;
    }
    // keep only digits, then insert slashes at MM/DD/YYYY positions
    const digits = raw.replace(/\D/g, "");
    let formatted = "";
    if (digits.length <= 2) formatted = digits;
    else if (digits.length <= 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    else formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;

    // set the visible input value (improves typing UX)
    if (e?.target && typeof e.target.value !== "undefined") {
      try {
        e.target.value = formatted;
      } catch {
        // ignore if read-only
      }
    }

    // if a full date entered, parse and call onChange with Date object
    if (formatted.length === 10 && onChange) {
      const [mm, dd, yyyy] = formatted.split("/");
      const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (!Number.isNaN(parsed.getTime())) {
        // normalize parsed date for comparison
        parsed.setHours(0, 0, 0, 0);

        // when futureNotAllowed and parsed > today, use today instead
        if (futureNotAllowed && parsed > today) {
          const useDate = new Date(today);
          // update visible input to today's date
          if (e?.target && typeof e.target.value !== "undefined") {
            try {
              e.target.value = formatDate(useDate);
            } catch {}
          }
          onChange(useDate);
        } else {
          onChange(parsed);
        }
      }
    }
  };

  // when futureNotAllowed is true, block dates after today; otherwise allow future dates
  const maxDate = futureNotAllowed ? today : undefined;

  return (
    <div className={futureNotAllowed ? "future-blocked" : ""}>
      {label && (
        <label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <ReactDatePicker
        selected={value}
        // intercept onChange from calendar selection to enforce futureNotAllowed
        onChange={(date) => {
          if (!date) return onChange(null);
          const d = new Date(date);
          d.setHours(0, 0, 0, 0);
          if (futureNotAllowed && d > today) {
            onChange(new Date(today));
          } else {
            onChange(d);
          }
        }}
        onChangeRaw={handleChangeRaw}
        className={`form-control ${error ? "is-invalid" : ""}`}
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        maxDate={maxDate}
        dayClassName={(date) =>
          futureNotAllowed && date > new Date() ? "react-datepicker__day--future" : ""
        }
      />

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default DatePicker;
